import { Box, Tab, Tabs } from "@mui/material";
import type { SyntheticEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeviceTier } from "../hooks/useDeviceTier";
import { useSurfaceAccess } from "../contexts/SurfaceAccessContext";
import type { SurfaceKey } from "../authz/surfaceAccess";

type NavItem = {
  key: string;
  text: string;
  legacy: boolean;
  link: string;
  surface: SurfaceKey;
};

export function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const deviceTier = useDeviceTier();
  const { canAccessSurface } = useSurfaceAccess();

  const tabItems: NavItem[] = [
    { key: "Home", text: "Home", legacy: false, link: "/", surface: "home" },
    { key: "Events", text: "Events", legacy: false, link: "/events", surface: "events" },
    { key: "Venues", text: "Venues", legacy: false, link: "/venues", surface: "venues" },
    { key: "Devices", text: "Devices", legacy: false, link: "/devices", surface: "devices" },
    { key: "Agents", text: "Agents", legacy: false, link: "/agents", surface: "agents" },
    { key: "Report", text: "Reports", legacy: false, link: "/report", surface: "reports" },
    { key: "People", text: "People", legacy: false, link: "/people", surface: "people" },
    { key: "Groups", text: "Groups", legacy: false, link: "/groups", surface: "groups" },
  ];

  const visibleTabItems = tabItems.filter((item) => canAccessSurface(item.surface));

  const handleTabChange = (_event: SyntheticEvent, selectedLink: string) => {
    navigate(selectedLink);
  };

  const selectedLink = (() => {
    const path = location.pathname;

    if (path === "/" || path === "") return "/";
    if (path.startsWith("/events") || path.startsWith("/event-") || path.startsWith("/zone-")) return "/events";
    if (path.startsWith("/venues") || path.startsWith("/venue-")) return "/venues";
    if (path.startsWith("/devices") || path.startsWith("/device-") || path.startsWith("/product-") || path.startsWith("/category-")) return "/devices";
    if (path.startsWith("/agents") || path.startsWith("/agent")) return "/agents";
    if (path.startsWith("/report")) return "/report";
    if (path.startsWith("/people")) return "/people";
    if (path.startsWith("/groups")) return "/groups";
    return "/";
  })();

  const tabValue = visibleTabItems.some((item) => item.link === selectedLink) ? selectedLink : false;

  return (
    <Box
      component="nav"
      aria-label="Primary app navigation"
      sx={{
        width: "100%",
        m: 0,
        p: 0,
        bgcolor: "var(--shell-nav-bg)",
        borderTop: "2px solid var(--shell-nav-border)",
        borderBottom: "1px solid var(--shell-nav-border)",
      }}
    >
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant={deviceTier === "desktop" ? "standard" : "scrollable"}
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Primary app sections"
        TabIndicatorProps={{ sx: { display: "none" } }}
        sx={{
          minHeight: "var(--gs-touch-target-min)",
          height: "var(--gs-touch-target-min)",
          maxWidth: "100%",
          "& .MuiTabs-flexContainer": {
            alignItems: "stretch",
            justifyContent: deviceTier === "desktop" ? "center" : "flex-start",
          },
          "& .MuiTabs-scroller": {
            minHeight: "var(--gs-touch-target-min)",
          },
          "& .MuiTab-root": {
            minHeight: "var(--gs-touch-target-min)",
            height: "var(--gs-touch-target-min)",
            minWidth: { xs: 84, sm: 96, md: 110 },
            px: { xs: 1.25, sm: 1.5, md: 2 },
            color: "var(--shell-nav-text)",
            fontSize: { xs: "0.875rem", md: "1rem" },
            fontWeight: 500,
            textTransform: "none",
            transition: (theme) =>
              theme.transitions.create(["background-color", "color"], {
                duration: theme.transitions.duration.shortest,
              }),
            "&:hover": {
              bgcolor: "var(--shell-nav-item-hover)",
            },
            "&.Mui-selected": {
              bgcolor: "var(--shell-nav-item-selected)",
              color: "var(--shell-nav-text)",
            },
          },
          "& .MuiTab-root[data-legacy='true']:not(.Mui-selected)": {
            color: "var(--shell-nav-muted-text)",
          },
          "& .MuiTabs-scrollButtons": {
            color: "rgba(255, 255, 255, 1)",
            minHeight: "var(--gs-touch-target-min)",
            width: "var(--gs-touch-target-min)",
          },
        }}
      >
        {visibleTabItems.map((item) => (
          <Tab
            key={item.key}
            value={item.link}
            label={item.text}
            data-legacy={item.legacy ? "true" : undefined}
          />
        ))}
      </Tabs>
    </Box>
  );
}

export default NavBar;
